import Styles from './TarifPage.module.css'
import Colors from 'Theme/Colors'
import { IFButton, IFCount, IFFilter, IFText } from 'Components'
import { useRef, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import TariffSelectors from 'Stores/Tariff/Selectors'
import TariffActions from 'Stores/Tariff/Actions'
import { connect } from 'react-redux'
import RequestState from 'Enums/RequestState'
import { TariffList, TariffModal } from 'Containers'
import PropTypes from 'prop-types'
import AuthSelectors from 'Stores/Auth/Selectors'
import InfinityEnums from 'Enums/InfinityEnums'
import LoadingBar from 'react-top-loading-bar'
import { useLocation } from 'react-router-dom'

const TariffPage = ({
  fetchTariffList,
  fetchTariffListRequestState,
  operatorUser,
  fetchTariffsCount,
  fetchTariffsCountRequestState,
  count,
  tariffFilter,
  resetTariffs,
  createTariffRequestState,
  createTariff,
  resetFilter,
}) => {
  const { t } = useTranslation()
  const [progress, setProgress] = useState(0)
  const [firstLoad, setFirstLoad] = useState(true)
  const [tablesHeight, setTablesHeight] = useState(0)
  const tariffTableRef = useRef(null)
  const tariffFilterRef = useRef(null)
  const createUserRef = useRef(null)
  const canEdit = operatorUser.permissions.includes(
    InfinityEnums.OperatorUserPermission.CAN_EDIT_TARIFF,
  )

  const location = useLocation()

  useEffect(() => {
    resetFilter()
    tariffFilterRef?.current?.clearFilter()
    fetchTariffList(null, 0)
    fetchTariffsCount(null)
  }, [])

  useEffect(() => {
    if (location.state?.refresh) {
      setFirstLoad(true)
      resetTariffs()
      resetFilter()
    }
  }, [location.state?.refresh])
  useEffect(() => {
    if (fetchTariffListRequestState === RequestState.LOADING && progress === 0)
      setProgress(progress + 10)
    if (
      (fetchTariffListRequestState !== RequestState.LOADING &&
        fetchTariffListRequestState !== RequestState.UNINITIALIZED &&
        fetchTariffListRequestState !== RequestState.SUCCEEDED) ||
      fetchTariffListRequestState === RequestState.SUCCEEDED
    )
      setProgress(100)
  }, [fetchTariffListRequestState])
  useEffect(() => {
    if (
      fetchTariffListRequestState === RequestState.SUCCEEDED &&
      fetchTariffsCountRequestState === RequestState.SUCCEEDED &&
      firstLoad
    ) {
      setFirstLoad(false)
    }
  }, [fetchTariffListRequestState, fetchTariffsCountRequestState, firstLoad])

  useEffect(() => {
    const handleResize = (entries) => {
      if (entries[0]) {
        const { height } = entries[0].contentRect
        setTablesHeight(height)
      }
    }
    const resizeObserver = new ResizeObserver(handleResize)
    if (tariffTableRef.current) {
      resizeObserver.observe(tariffTableRef.current)
    }

    return () => {
      if (tariffTableRef.current) {
        resizeObserver.unobserve(tariffTableRef.current)
      }
    }
  })

  const handleFilterChange = (newFilter) => {
    resetTariffs()
    fetchTariffList(newFilter, 0)
    fetchTariffsCount(newFilter)
  }

  const handleTariffSubmit = (tariff) => {
    createTariff(tariff)
  }
  useEffect(() => {
    if (createTariffRequestState === RequestState.SUCCEEDED) {
      createUserRef.current.dismiss()
    }
  }, [createTariffRequestState])

  return (
    <div className={Styles.Wrapper}>
      <LoadingBar
        color={Colors.primary}
        progress={progress}
        onLoaderFinished={() => setProgress(0)}
      />

      <div
        className={Styles.Container}
        style={{
          backgroundColor: Colors.white,
          borderColor: Colors.DividerColor,
        }}
      >
        <div className={Styles.TitleContainer}>
          <div className={Styles.TitleDiv}>
            <IFText
              className={Styles.TitleText}
              loadSkeleton={
                fetchTariffListRequestState === RequestState.LOADING &&
                firstLoad
              }
              skeletonWidth={'5rem'}
            >
              {t('TariffPage.Title')}
            </IFText>
            <IFCount
              count={count}
              isLoading={fetchTariffsCountRequestState === RequestState.LOADING}
            />
          </div>
          <div>
            {canEdit && (
              <IFButton
                size="xsm"
                color={Colors.primary}
                isFill={false}
                isLoading={false}
                text={t('TariffPage.Create')}
                className={Styles.CreateButton}
                onClick={() => createUserRef.current.show()}
                loadSkeleton={
                  fetchTariffListRequestState === RequestState.LOADING &&
                  firstLoad
                }
              />
            )}
          </div>
        </div>
        <div className={Styles.FilterDiv}>
          <IFFilter
            ref={tariffFilterRef}
            onFilterChange={(newFilter) => handleFilterChange(newFilter)}
            filters={tariffFilter}
            textFieldPlaceholder={t('TariffPage.FilterPlaceholder')}
            isLoading={
              firstLoad && fetchTariffListRequestState === RequestState.LOADING
            }
          />
        </div>

        <div className={Styles.Tables} ref={tariffTableRef}>
          <TariffList tablesHeight={tablesHeight} />
        </div>
      </div>
      <TariffModal
        ref={createUserRef}
        onSubmit={handleTariffSubmit}
        createTariffRequestState={createTariffRequestState}
        operator={operatorUser.operator}
      />
    </div>
  )
}

function mapDispatchToProps(dispatch) {
  return {
    fetchTariffList: (filter, offset) =>
      dispatch(TariffActions.fetchTariffList(filter, offset)),
    fetchTariffsCount: (filter) =>
      dispatch(TariffActions.fetchTariffsCount(filter)),

    resetTariffs: () => dispatch(TariffActions.resetTariffs()),
    createTariff: (tariff) => dispatch(TariffActions.createTariff(tariff)),
    resetFilter: () => dispatch(TariffActions.resetFilter()),
  }
}

const mapStateToProps = (state) => ({
  fetchTariffListRequestState:
    TariffSelectors.getFetchTariffListRequestState(state),

  operatorUser: AuthSelectors.getOperatorUser(state),

  fetchTariffsCountRequestState:
    TariffSelectors.getFetchTariffsCountRequestState(state),
  count: TariffSelectors.getCount(state),
  tariffFilter: TariffSelectors.getTariffFilter(state),
  createTariffRequestState: TariffSelectors.getCreateTariffRequestState(state),
})

TariffPage.propTypes = {
  fetchTariffList: PropTypes.func,
  fetchTariffListRequestState: PropTypes.number,
  fetchInviteOperatorUserList: PropTypes.func,
  operatorUser: PropTypes.object,
  fetchTariffsCount: PropTypes.func,
  fetchTariffsCountRequestState: PropTypes.number,
  count: PropTypes.number,
  tariffFilter: PropTypes.object,
  resetTariffs: PropTypes.func,
  createTariffRequestState: PropTypes.number,
  createTariff: PropTypes.func,
}

export default connect(mapStateToProps, mapDispatchToProps)(TariffPage)
